import { useModal } from '@ebay/nice-modal-react'
import { useEffect, useState } from 'react'

import useAppDownload from '../../hooks/queries/useAppDownload'
import useDeviceDetect from '../../hooks/useDeviceDetect'
import { useBoundStore } from '../../store/store'
import { isPWA } from '../../utils/device'
import { isInNativeApp } from '../../utils/nativeBridge'
import { loadPwaTutorialVideo } from '../../utils/pwaTutorialVideo'
import Icon from '../icon'
import PwaDownloadStepsModal from './pwa-download-steps-modal'
import PWATutorial from './pwa-tutorial'

const STORAGE_KEY = 'download-app-dismissed'

const DownloadAppModal = () => {
  // The downloaded app is casino-branded; pick assets by casinoTheme even on
  // sports/sponsors routes where the visible theme differs.
  const theme = useBoundStore(state => state.common.casinoTheme)
  const [isVisible, setIsVisible] = useState(false)
  const downloadLinks = useAppDownload()
  const pwaDownloadStepsModal = useModal(PwaDownloadStepsModal)
  const pwaTutorial = useModal(PWATutorial)
  const { isIOS, isMobile } = useDeviceDetect()

  useEffect(() => {
    if (!isMobile || isPWA() || isInNativeApp()) return

    const dismissed = localStorage.getItem(STORAGE_KEY)
    if (dismissed) {
      const dismissedDate = new Date(dismissed).toDateString()
      const today = new Date().toDateString()
      if (dismissedDate === today) return
    }

    setIsVisible(true)
    // isMobile flips false->true once the lazy device-detect chunk warms; rerun
    // so the prompt still appears on mobile if this mounted pre-warm.
  }, [isMobile])

  const dismiss = () => {
    setIsVisible(false)
    localStorage.setItem(STORAGE_KEY, new Date().toISOString())
  }

  if (!isVisible) return null

  const handleInstall = async () => {
    // There is no Apple build any more: iPhone/iPad users are walked through
    // installing the PWA instead of being sent to an App Store download. The
    // video tutorial is the primary flow; the static step images stand in when
    // the current browser has no recording.
    if (isIOS) {
      let video: string
      try {
        video = await loadPwaTutorialVideo()
      } catch {
        // The recording is its own chunk, so the fetch can fail — offline, or
        // a stale hashed asset after a deploy. Leave the prompt on screen so
        // the tap stays retryable: dismissing here would burn the daily
        // suppression on a tap that showed nothing, and this prompt is the
        // only way back into the install flow.
        return
      }

      dismiss()
      void (video
        ? pwaTutorial.show({ pwa_tutorial: video })
        : pwaDownloadStepsModal.show())
      return
    }

    const link =
      theme === 'dark'
        ? downloadLinks.adjust.download_link_dark
        : downloadLinks.adjust.download_link_light
    const fallback =
      downloadLinks.google_play_android.download_link ||
      downloadLinks.android.download_link

    const target = link || fallback
    if (target) window.open(target, '_blank')
  }

  return (
    <div className="fixed inset-0 z-[300]">
      <div
        className="absolute inset-0 bg-black/50 animate-fadeIn"
        onClick={dismiss}
      />
      <div className="absolute bottom-20 left-0 right-0 animate-slideUp">
        <div className="relative mx-3 max-w-[500px] rounded-2xl px-6 pt-5 pb-6 border border-solid theme-border bg-primary dark:bg-primary-bg">
          {/* Decorative top glow */}
          <div className="theme-shimmer absolute top-0 left-0 right-0 h-[1px]" />

          {/* Close button */}
          <button
            aria-label="Cerrar"
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-white/10"
            onClick={dismiss}>
            <Icon id="close" width="10" height="10" fill="#999" />
          </button>

          {/* Title */}
          <h3 className="text-highlight dark:text-gold-txt text-xl font-bold text-center">
            {isIOS ? 'Instala la app' : 'Descarga la app'}
          </h3>

          {/* Subtitle */}
          <p className="text-txt text-sm text-center mt-1">
            para retiro instantáneo{' '}
            <span className="text-highlight dark:text-gold-txt font-semibold">
              más rápido y seguro
            </span>
          </p>

          {/* Features */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <div className="flex-auto flex items-center gap-1.5">
              <span className="text-base">🚀</span>
              <span className="text-txt text-sm">
                Más rápido{' '}
                <span className="text-highlight dark:text-gold-txt">
                  en la app
                </span>
              </span>
            </div>
            <div className="flex-auto flex items-center gap-1.5">
              <span className="text-base">✅</span>
              <span className="flex-none text-highlight dark:text-gold-txt text-sm">
                Más seguro
              </span>
            </div>
          </div>

          {/* Download button */}
          <button
            className="w-full mt-5 py-3 rounded-lg text-base font-bold uppercase text-white tracking-wider border-solid border-2 theme-cta-accent"
            style={{
              background:
                'linear-gradient(180deg, #4caf50 0%, #2e7d32 50%, #1b5e20 100%)',
            }}
            onClick={() => void handleInstall()}>
            {isIOS ? 'INSTALAR AHORA' : 'DESCARGAR AHORA'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DownloadAppModal
